import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { getCargoModuleStats, getCargoWeights, getCargoBreakdown } from "@/lib/api";
import { useRefreshOnFocus } from "@/hooks/useRefreshOnFocus";
import { STATUS_STYLES } from "@/lib/cargoFields";
import {
  Package, ArrowDownToLine, ArrowUpFromLine, CalendarClock, Plus, History,
  CalendarRange, Scale, Cross, Stamp, ChevronRight, X,
} from "lucide-react";
import { DirectionBadge } from "@/components/DirectionBadge";
import { CargoEntryDialog } from "@/components/CargoEntryDialog";
import { CargoMonthlyChart } from "@/components/CargoMonthlyChart";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const MONTHS = [
  { v: "all", l: "All months" }, { v: "1", l: "January" }, { v: "2", l: "February" },
  { v: "3", l: "March" }, { v: "4", l: "April" }, { v: "5", l: "May" }, { v: "6", l: "June" },
  { v: "7", l: "July" }, { v: "8", l: "August" }, { v: "9", l: "September" },
  { v: "10", l: "October" }, { v: "11", l: "November" }, { v: "12", l: "December" },
];

const fmtKg = (n) =>
  n == null ? "—" : `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg`;

const StatCard = ({ icon: Icon, label, value, tone, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-slate-500 text-sm">{label}</p>
        <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${tone}`}>
        <Icon size={24} />
      </div>
    </div>
  </div>
);

const WeightTile = ({ icon: Icon, label, value, tone, onClick, testid }) => (
  <div
    className={`bg-white rounded-xl border border-slate-200 p-4 shadow-sm transition-all ${
      onClick ? "cursor-pointer hover:border-emerald-300 hover:shadow-md" : ""
    }`}
    onClick={onClick}
    data-testid={testid}
  >
    <div className="flex items-center justify-between">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone}`}>
        <Icon size={18} />
      </div>
      {onClick && <ChevronRight size={16} className="text-slate-300" />}
    </div>
    <p className="text-slate-500 text-xs mt-3">{label}</p>
    <p className="text-xl font-bold text-slate-900 mt-0.5">{value}</p>
  </div>
);

const MiniTable = ({ title, icon: Icon, rows, empty, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid={testid}>
    <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><Icon size={18} className="text-emerald-500" /> {title}</h2>
    {rows?.length ? (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-4">Date</th>
              <th className="py-2 pr-4">Airline</th>
              <th className="py-2 pr-4">Flight</th>
              <th className="py-2 pr-4">AWB</th>
              <th className="py-2 pr-4">Dir</th>
              <th className="py-2 pr-4">Route</th>
              <th className="py-2 pr-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id} className="border-b border-slate-100">
                <td className="py-2 pr-4 whitespace-nowrap">{e.shipment_date || "—"}</td>
                <td className="py-2 pr-4">{e.airline || "—"}</td>
                <td className="py-2 pr-4">{e.flight_no || "—"}</td>
                <td className="py-2 pr-4 font-mono">{e.shipment_id || "—"}</td>
                <td className="py-2 pr-4"><DirectionBadge dir={e.direction} /></td>
                <td className="py-2 pr-4">{[e.origin, e.destination].filter(Boolean).join(" → ") || "—"}</td>
                <td className="py-2 pr-4"><span className={`px-2 py-0.5 rounded border text-xs capitalize ${STATUS_STYLES[e.status] || ""}`}>{e.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <p className="text-slate-400 text-sm">{empty}</p>
    )}
  </div>
);

function BreakdownDialog({ open, onClose, kind, title, year, initialDirection, onChanged }) {
  const [month, setMonth] = useState("all");
  const [direction, setDirection] = useState(initialDirection || "all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [reload, setReload] = useState(0);
  const closeEntry = useCallback(() => setOpenId(null), []);
  const onSaved = useCallback(() => { setReload((n) => n + 1); onChanged?.(); }, [onChanged]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = { kind };
    if (year && year !== "all") params.year = year;
    if (month !== "all") params.month = month;
    if (direction !== "all") params.direction = direction;
    getCargoBreakdown(params)
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open, kind, year, month, direction, reload]);

  const totalWeight = rows.reduce((s, r) => s + (Number(r.weight_kg) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="breakdown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{title}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-3 mb-2">
          <div className="text-sm text-slate-500">
            Year: <span className="font-semibold text-slate-800">{year === "all" ? "All time" : year}</span>
          </div>
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
            data-testid="breakdown-month-filter"
          >
            {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
            data-testid="breakdown-direction-filter"
          >
            <option value="all">Import & Export</option>
            <option value="import">Import</option>
            <option value="export">Export</option>
          </select>
          <div className="ml-auto flex items-center gap-4 text-sm">
            <span className="text-slate-500">Shipments: <span className="font-bold text-slate-900" data-testid="breakdown-count">{rows.length}</span></span>
            <span className="text-slate-500">Total weight: <span className="font-bold text-slate-900">{fmtKg(totalWeight)}</span></span>
          </div>
        </div>

        <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">AWB</th>
                <th className="px-3 py-2">Dir</th>
                <th className="px-3 py-2">Airline</th>
                <th className="px-3 py-2">Flight</th>
                <th className="px-3 py-2">Route</th>
                <th className="px-3 py-2 text-right">Weight</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400" data-testid="breakdown-empty">No shipments match these filters.</td></tr>}
              {!loading && rows.map((e, i) => (
                <tr key={e.id || i} onClick={() => e.id && setOpenId(e.id)} className="border-t border-slate-100 cursor-pointer hover:bg-emerald-50/60 transition-colors" data-testid={`breakdown-row-${i}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{e.shipment_date || "—"}</td>
                  <td className="px-3 py-2 font-mono">{e.shipment_id || "—"}</td>
                  <td className="px-3 py-2"><DirectionBadge dir={e.direction} /></td>
                  <td className="px-3 py-2">{e.airline || "—"}</td>
                  <td className="px-3 py-2">{e.flight_no || "—"}</td>
                  <td className="px-3 py-2">{[e.origin, e.destination].filter(Boolean).join(" → ") || "—"}</td>
                  <td className="px-3 py-2 text-right font-semibold">{e.weight_kg != null ? `${e.weight_kg}` : "—"}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded border text-xs capitalize ${STATUS_STYLES[e.status] || ""}`}>{e.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-400">Tap a shipment to see its full details and edit it.</p>
        {openId && <CargoEntryDialog entryId={openId} onClose={closeEntry} onSaved={onSaved} />}
      </DialogContent>
    </Dialog>
  );
}

export default function CargoDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [weights, setWeights] = useState(null);
  const [year, setYear] = useState("all");
  const [breakdown, setBreakdown] = useState(null); // {kind, title, direction}

  const loadStats = useCallback(() => {
    getCargoModuleStats()
      .then((res) => setStats(res.data))
      .catch(() => setStats({ total: 0, imports: 0, exports: 0, by_status: {}, today: 0, past: [], future: [] }));
  }, []);

  const loadWeights = useCallback(() => {
    getCargoWeights(year)
      .then((res) => setWeights(res.data))
      .catch(() => setWeights(null));
  }, [year]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadWeights(); }, [loadWeights]);
  useRefreshOnFocus(() => { loadStats(); loadWeights(); });

  const years = weights?.years || [];

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="cargo-dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Package className="text-emerald-500" /> Cargo Dashboard
          </h1>
          <p className="text-slate-500 text-sm mt-1">Latest past shipments and upcoming cargo at a glance.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/cargo/planning")} data-testid="go-planning-btn">
            <CalendarRange size={18} className="mr-2" /> Global Planning
          </Button>
          <Button onClick={() => navigate("/cargo/planning")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cargo-new-entry-btn">
            <Plus size={18} className="mr-2" /> New Entry
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Package} label="Total Shipments" value={stats?.total ?? "—"} tone="bg-slate-100 text-slate-700" testid="stat-total" />
        <StatCard icon={ArrowDownToLine} label="Imports" value={stats?.imports ?? "—"} tone="bg-blue-100 text-blue-700" testid="stat-imports" />
        <StatCard icon={ArrowUpFromLine} label="Exports" value={stats?.exports ?? "—"} tone="bg-amber-100 text-amber-700" testid="stat-exports" />
        <StatCard icon={CalendarClock} label="Today" value={stats?.today ?? "—"} tone="bg-emerald-100 text-emerald-700" testid="stat-today" />
      </div>

      {/* Weight totals + T1 */}
      <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 md:p-5" data-testid="weights-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Scale size={18} className="text-emerald-500" /> Cargo Weights & Status
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Period</span>
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm font-medium"
              data-testid="weights-year-filter"
            >
              <option value="all">All time</option>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <WeightTile icon={ArrowDownToLine} label="Import Cargo Weight" value={fmtKg(weights?.import_cargo_weight)} tone="bg-blue-100 text-blue-700" testid="weight-import-cargo" />
          <WeightTile icon={ArrowUpFromLine} label="Export Cargo Weight" value={fmtKg(weights?.export_cargo_weight)} tone="bg-amber-100 text-amber-700" testid="weight-export-cargo" />
          <WeightTile
            icon={Cross} label="Import Human Remains" value={fmtKg(weights?.import_hum_weight)} tone="bg-rose-100 text-rose-700"
            onClick={() => setBreakdown({ kind: "hum", title: "Import Human Remains (HUM)", direction: "import" })}
            testid="weight-import-hum"
          />
          <WeightTile
            icon={Cross} label="Export Human Remains" value={fmtKg(weights?.export_hum_weight)} tone="bg-rose-100 text-rose-700"
            onClick={() => setBreakdown({ kind: "hum", title: "Export Human Remains (HUM)", direction: "export" })}
            testid="weight-export-hum"
          />
          <WeightTile icon={Scale} label="General Total Weight" value={fmtKg(weights?.total_weight)} tone="bg-slate-200 text-slate-700" testid="weight-total" />
          <WeightTile
            icon={Stamp} label="T1 Status Cargo" value={weights?.t1_count ?? "—"} tone="bg-indigo-100 text-indigo-700"
            onClick={() => setBreakdown({ kind: "t1", title: "T1 Status Cargo", direction: "all" })}
            testid="weight-t1"
          />
        </div>
        <p className="text-[11px] text-slate-400 mt-3">Cargo weights exclude human remains, which are totalled separately. T1 includes every shipment with an MRN number. Tap the HUM or T1 tiles to see the shipments behind them.</p>
      </div>

      <CargoMonthlyChart year={year} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MiniTable title="Latest Past Cargo" icon={History} rows={stats?.past} empty="No past shipments." testid="past-table" />
        <MiniTable title="Upcoming Cargo" icon={CalendarClock} rows={stats?.future} empty="No upcoming shipments scheduled." testid="future-table" />
      </div>

      {breakdown && (
        <BreakdownDialog
          open
          onClose={() => setBreakdown(null)}
          kind={breakdown.kind}
          title={breakdown.title}
          year={year}
          initialDirection={breakdown.direction}
          onChanged={() => { loadStats(); loadWeights(); }}
        />
      )}
    </div>
  );
}
